/**
 * `<DeleteCandidateModal>` — type-to-confirm delete modal.
 *
 * Spec: candidate-detail-rebuild-spec.md §8 / global-patterns §1.4.
 *
 * The destructive button enables only when the user has typed the candidate's
 * full name exactly (literal match, case-sensitive per spec §8).
 *
 * Default focus: Cancel (the safe action), per global-patterns §1.4.
 * Redirects to Dashboard after successful delete (parent handles navigation).
 */
import { useState, type JSX, type ChangeEvent } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/common/Button";

interface DeleteCandidateModalProps {
  candidateName: string;
  /**
   * Count of JD pairings, used to inform the copy. Pass `null` when the count
   * is not known to the caller (e.g. the tenant-wide list view, which would
   * need an N+1 per-row fetch to learn it) — the copy then falls back to a
   * count-agnostic phrasing.
   */
  pairingCount: number | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DeleteCandidateModal({
  candidateName,
  pairingCount,
  isPending,
  onConfirm,
  onCancel,
}: DeleteCandidateModalProps): JSX.Element {
  const [inputValue, setInputValue] = useState("");

  const confirmMatches = inputValue === candidateName;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const pairingCopy =
    pairingCount === null
      ? "every pipeline pairing"
      : pairingCount === 1
        ? "1 pipeline pairing"
        : `${pairingCount} pipeline pairings`;

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-candidate-title"
      onKeyDown={handleKeyDown}
      onClick={onCancel}
      data-testid="delete-candidate-modal"
    >
      <div
        className="w-full max-w-md rounded-xl border border-border-default bg-surface-elevated p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start gap-3">
          <AlertTriangle
            size={20}
            className="mt-0.5 flex-shrink-0 text-status-rejected-fg"
            aria-hidden
          />
          <h2
            id="delete-candidate-title"
            className="text-lg font-semibold text-text-emphasis"
          >
            Delete this candidate?
          </h2>
        </div>

        <p className="mb-6 text-sm text-text-secondary">
          <strong className="font-medium text-text-primary">{candidateName}</strong>{" "}
          will be permanently removed across all jobs in your tenant — that&apos;s{" "}
          {pairingCopy}, all evaluations, and all notes. This can&apos;t be undone.
        </p>

        <div className="mb-6">
          <label
            htmlFor="delete-confirm-input"
            className="mb-1.5 block text-xs font-medium text-text-secondary"
          >
            Type{" "}
            <span className="font-semibold text-text-primary">
              {candidateName}
            </span>{" "}
            to confirm
          </label>
          <input
            id="delete-confirm-input"
            type="text"
            value={inputValue}
            onChange={handleChange}
            placeholder={candidateName}
            autoComplete="off"
            className="w-full rounded-md border border-border-strong bg-surface-subtle px-3 py-2 text-sm text-text-primary placeholder-text-tertiary focus:border-[var(--border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--border-focus)]"
            data-testid="delete-confirm-input"
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            autoFocus
            data-testid="delete-candidate-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!confirmMatches || isPending}
            loading={isPending}
            onClick={onConfirm}
            data-testid="delete-candidate-confirm"
          >
            Delete permanently
          </Button>
        </div>
      </div>
    </div>
  );
}
